<template>
  <div v-if="customIsVisible" :class="[backgroundColor, bottomMargin, verticalPadding, background]" class="downloads">
    <div class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <VcTypography v-if="title" :tag="heading" :anchor-id="title" class="w-full mb-8">
        {{ title }}
      </VcTypography>
      <div class="flex flex-wrap w-full">
        <div
          v-for="download in enrichedDownloads"
          class="flex flex-col w-full items-center border-b border-dotted last-of-type:border-none"
        >
          <!-- Innovadis custom: sanitize download URL before binding to href -->
          <a
            v-if="
              download.downloadFileFormat.toLocaleLowerCase() !== 'exe' &&
              download.downloadFileFormat.toLocaleLowerCase() !== 'msi'
            "
            :href="getSafeDownloadUrl(download.downloadFile)"
            :download="download.downloadTitle"
            target="_blank"
            rel="noopener"
            class="flex flex-row justify-between w-full py-2"
          >
            <span class="flex flex-row items-center"
              >{{ download.downloadTitle }}
              <VcImage
                v-if="showFlag(download.downloadLanguage)"
                :src="`/static/icons/flags/${download.downloadLanguage.toLocaleLowerCase()}.svg`"
                :alt="download.downloadLanguage"
                class="language-selector__img ml-2"
                lazy
            /></span>
            <div class="grow"></div>
            <!-- <span class="flex w-8 h-full text-right ml-2 items-center" v-if="download.downloadFileSize !== '0'">{{
              download.downloadFileSize
            }}</span> -->
            <div class="ralative flex w-8 h-full ml-2 text-right items-center">
              <VcIcon name="file-file" size="lg" :title="download.downloadFileFormat.toLocaleLowerCase()" />
              <div
                class="absolute text-white text-xs px-1 py-[1px] rounded bg-unknown"
                :class="`bg-${download.downloadFileFormat.toLocaleLowerCase()}`"
              >
                {{ download.downloadFileFormat.toUpperCase() }}
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { getSafeUrl } from "@/core/utilities/url/safeUrl";

type DownloadItemType = {
  downloadTitle: string;
  downloadFile: string;
  downloadLanguage: string;
};

type DownloadType = {
  downloadTitle: string;
  downloadFile: string;
  downloadLanguage: string;
  downloadFileSize: string;
  downloadFileFormat: string;
};

interface IProps {
  id: string;
  heading?: string;
  title?: string;
  downloads?: DownloadItemType[];

  background?: string;
  model: any;
  settings: any;

  // Innovadis custom: Use custom visibility setting
  customIsVisible: boolean;

  // Innovadis custom: Use custom layout settings
  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
});

const enrichedDownloads = ref<DownloadType[]>([]);

// Innovadis custom: Sanitize download URLs to prevent XSS attacks via javascript: scheme injection
function getSafeDownloadUrl(url: string | undefined): string {
  return getSafeUrl(url, { allowedProtocols: ["http", "https"] });
}

function getFileType(url: string | null | undefined): string {
  if (typeof url !== "string" || url.trim() === "") {
    return "Unknown";
  }
  const parts = url.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "Unknown";
}

function showFlag(language: string | undefined): boolean {
  return !!language && language !== "n/a";
}

// function formatFileSize(bytes: number | undefined) {
//   if (!bytes) return "Unknown size";
//   const sizes = ["B", "KB", "MB", "GB", "TB"];
//   let i = 0;
//   let size = bytes;
//   while (size >= 1024 && i < sizes.length - 1) {
//     size /= 1024;
//     i++;
//   }
//   return size.toFixed(2) + " " + sizes[i];
// }

// async function fetchFileSize(url: string) {
//   try {
//     const response = await fetch(url, {
//       method: "HEAD",
//     });
//     const size = response.headers.get("Content-Length");
//     return formatFileSize(size ? parseInt(size, 10) : 0);
//   } catch (error) {
//     console.error("Error fetching file size for", url, error);
//     return 0;
//   }
// }

onMounted(async () => {
  if (!props.downloads) return;

  const enriched = await Promise.all(
    props.downloads.map(async (download) => {
      const fileSize = 0; // await fetchFileSize(download.downloadFile);  Disabled for now due to CORS errors which dont seem to be fixed easily by back-end team.
      const fileType = getFileType(download.downloadFile);

      return {
        downloadTitle: download.downloadTitle,
        downloadFile: download.downloadFile,
        downloadLanguage: download.downloadLanguage,
        downloadFileSize: fileSize.toString(),
        downloadFileFormat: fileType,
      };
    }),
  );

  enrichedDownloads.value = enriched;
});
</script>
<style type="scss">
/* ADD CLASSES TO SUPPORT MORE EXTENSION COLORS - these colors are based on ChatGpt suggestions */

.bg-ai {
  background-color: #ff9a00 !important;
}
.bg-csv,
.bg-xml,
.bg-xsd,
.bg-json,
.bg-ini,
.bg-cfg {
  background-color: #00796b !important;
}
.bg-doc,
.bg-docx {
  background-color: #1976d2 !important;
}
.bg-eot,
.bg-woff,
.bg-woff2,
.bg-ttf,
.bg-otf {
  background-color: #607d8b !important;
}
.bg-excel {
  background-color: #00a859 !important;
}
.bg-jpg,
.bg-jpeg {
  background-color: #c2185b !important;
}
.bg-js,
.bg-htm,
.bg-html,
.bg-scss,
.bg-py {
  background-color: #4caf50 !important;
}
.bg-mkv {
  background-color: #455a64 !important;
}
.bg-mp3 {
  background-color: #e91e63 !important;
}
.bg-mp4 {
  background-color: #c62828 !important;
}
.bg-pdf {
  background-color: #d32f2f !important;
}
.bg-png {
  background-color: #0097a7 !important;
}
.bg-ppt,
.bg-pptx {
  background-color: #f57c00 !important;
}
.step,
.stp,
.iges,
.igs,
.dwg,
.dxf,
.sldprt,
.sldasm,
.stl,
.\33 3dxml {
  background-color: #a1887f !important;
}
.bg-svg {
  background-color: #512da8 !important;
}
.bg-tiff {
  background-color: #800080 !important;
}
.bg-txt {
  background-color: #1e90ff !important;
}
.bg-unknown {
  background-color: #c0c0c0; /* default color for unsupported extensions */
}
.bg-wav {
  background-color: #0288d1 !important;
}
.bg-zip,
.bg-rar,
.bg-7z {
  background-color: #7b1fa2 !important;
}
</style>
